/**
 * Routes `/bear/me` and `/bear/player/:playerId` (spec section 27).
 *
 * `/bear/me` resolves through `GET /api/me`, which reads the authoritative
 * `app_users` row. An account with no linked player is a normal state — a
 * viewer, or a member nobody has linked yet — and gets an explanation rather
 * than an error.
 */

import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAllianceMembers, useBearAnalysis, useCurrentUser } from '../../api/queries';
import { useCoreContext } from '../../lib/coreAlliance';
import { Card, PageHeader, Value } from '../../components/ui/primitives';
import { EmptyState, LoadingBlock, QueryBoundary, ErrorState } from '../../components/ui/states';
import { useT } from '../../i18n';
import { BearAnalysisView } from './PersonalBearAnalyzer';

export function MyBearPage() {
  const me = useCurrentUser();
  const t = useT();

  if (me.isPending) return <LoadingBlock label={t.bearAnalysis.resolvingAccount} />;
  if (me.error) return <ErrorState error={me.error} onRetry={() => void me.refetch()} />;

  if (!me.data?.playerId) return <NotLinkedYet />;

  return <BearAnalysisFor playerId={me.data.playerId} title={t.bearAnalysis.myTitle} />;
}

/**
 * An account with no linked player.
 *
 * This is the ordinary state on day one, not an error: linking an account to an
 * in-game player is a manual step an R5 performs. So the screen explains what
 * is missing and who fixes it, and — because the analysis itself works fine for
 * anyone — offers a way through to another player's analysis in the meantime.
 */
function NotLinkedYet() {
  const navigate = useNavigate();
  const core = useCoreContext();
  const members = useAllianceMembers(core.allianceId ?? undefined);
  const roster = members.data?.members ?? [];
  const t = useT();

  return (
    <>
      <PageHeader title={t.bearAnalysis.myTitle} />
      <div className="stack">
        <EmptyState
          title={t.bearAnalysis.notLinkedTitle}
          body={t.bearAnalysis.notLinkedBody}
        />

        <Card title={t.bearAnalysis.meantimeTitle}>
          <p className="muted small" style={{ marginTop: 0 }}>
            {t.bearAnalysis.meantimeBody}
          </p>
          {roster.length === 0 ? (
            <Link className="btn btn--sm" to="/alliance/members">
              {t.bearAnalysis.browseMembers}
            </Link>
          ) : (
            <label className="row" style={{ gap: 6 }}>
              <span className="muted small">{t.bearAnalysis.openAnalysisFor}</span>
              <select
                defaultValue=""
                onChange={(event) => {
                  if (event.target.value) {
                    navigate(`/bear/player/${encodeURIComponent(event.target.value)}`);
                  }
                }}
                style={{ minWidth: 200 }}
              >
                <option value="">{t.bearAnalysis.selectPlayer}</option>
                {[...roster]
                  .sort((a, b) => (a.name ?? a.id).localeCompare(b.name ?? b.id, undefined, {
                    sensitivity: 'base',
                  }))
                  .map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.name ?? member.id}
                    </option>
                  ))}
              </select>
            </label>
          )}
        </Card>
      </div>
    </>
  );
}

export function PlayerBearPage() {
  const { playerId } = useParams<{ playerId: string }>();
  const t = useT();

  if (!playerId) {
    return (
      <EmptyState title={t.bearAnalysis.noPlayerTitle} body={t.bearAnalysis.noPlayerBody} />
    );
  }

  return <BearAnalysisFor playerId={playerId} title={t.bearAnalysis.playerTitle} />;
}

function BearAnalysisFor({ playerId, title }: { playerId: string; title: string }) {
  const analysis = useBearAnalysis(playerId);
  const t = useT();

  return (
    <>
      <PageHeader
        title={title}
        subtitle={
          analysis.data && !analysis.data.empty ? (
            <>
              <Value absent={t.common.unnamedPlayer}>{analysis.data.analysis.playerName}</Value> ·{' '}
              <Link to={`/players/${encodeURIComponent(playerId)}`}>
                {t.bearAnalysis.openPlayerDetailInline}
              </Link>
            </>
          ) : (
            <Link to={`/players/${encodeURIComponent(playerId)}`}>
              {t.bearAnalysis.openPlayerDetail}
            </Link>
          )
        }
      />
      <QueryBoundary query={analysis} loadingLabel={t.bearAnalysis.loading}>
        {(data) => <BearAnalysisView result={data} />}
      </QueryBoundary>
    </>
  );
}
